import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { SelectionStyle } from '@common/prosemirror/commands/selection-format.command';
import { TextEditorCssStylesService } from '@portal-core/project-files/services/text-editor-css-styles.service';
import { CssCode, PreviewCssStyle } from '@portal-core/project-files/stylesheets/models/preview-css-style.model';
import { TextEditorService } from '@portal-core/text-editor/services/text-editor.service';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { map, Observable, switchMap } from 'rxjs';

/** Represents a view for collaborative editing of a stylesheet file. */
@Component({
  selector: 'mc-css-style-list',
  templateUrl: './css-style-list.component.html',
  styleUrls: ['./css-style-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CssStyleListComponent {

  @Input() cssStyles: PreviewCssStyle[] = [];
  @Input() filePath: string;
  @Input() readonly?: boolean = false;
  @Input() selectionStyle: SelectionStyle;

  @InputObservable('filePath') filePath$: Observable<string>;

  @Output() styleSelected: EventEmitter<string> = new EventEmitter<string>();

  previewStylesContainerId$: Observable<string>;
  showStyleFilter: boolean = false;
  styleFilterValue: string;

  selectionStyleCode$: Observable<CssCode>;

  private codePopoverShowTimeout: number = 0;
  private codePopoverHideTimeout: number = 0;
  private codePopoverShown: HTMLElement | null = null;

  get isUnwrapCommandAvailable(): boolean {
    return !!this.selectionStyle?.isInlineNodeSelection;
  }

  get isTextSelection(): boolean {
    return !!this.selectionStyle?.isInlineSelection;
  }

  get selectedStyleName(): string {
    return this.selectionStyle?.selectedStyleName;
  }

  constructor(
    private textEditorService: TextEditorService,
    private textEditorCssStyleService: TextEditorCssStylesService,
  ) {
    this.previewStylesContainerId$ = this.filePath$.pipe(
      switchMap(filePath => filePath ? this.textEditorService.buildFilePathHex(filePath) : Promise.resolve(undefined)),
      map(filePathHex => filePathHex ? this.textEditorService.buildPreviewStylesheetContainerId(filePathHex) : undefined)
    );
    this.selectionStyleCode$ = this.textEditorCssStyleService.applicableStylesCode$;
  }

  /**
   * Prevents re-rendering of the style item if the style item hasn't actually changed.
   * The notification's id is used to determine if a notification has changed.
   */
  trackById(index: number, style: PreviewCssStyle): string {
    return `${style.name}`;
  }

  isSelectedStyle(styleName: string): boolean {
    return this.selectedStyleName === styleName;
  }

  selectStyle(event: MouseEvent, styleName: string): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.readonly)
      this.styleSelected.emit(styleName);
  }

  resetClass(): void {
    if (!this.readonly)
      this.styleSelected.emit('');
  }

  unwrapText(): void {
    if (!this.readonly)
      this.styleSelected.emit(null);
  }

  /** Shows the code popover after a 400ms delay. Closes any other popover that's currently open so only one is visible at a time. */
  showCodePopover(popoverEl: HTMLElement) {
    clearTimeout(this.codePopoverHideTimeout);
    clearTimeout(this.codePopoverShowTimeout);
    // Clearing the hide timer above keeps a different popover open if one was pending close — explicitly close it here.
    if (this.codePopoverShown && this.codePopoverShown !== popoverEl) {
      this.codePopoverShown.hidePopover?.();
      this.codePopoverShown = null;
    }
    this.codePopoverShowTimeout = window.setTimeout(() => {
      popoverEl.showPopover?.();
      this.codePopoverShown = popoverEl;
    }, 400);
  }

  /**
   * Schedules hiding the popover after a short grace period.
   * The host's transparent left padding already covers the visual offset to the anchor item, so item↔host
   * transitions fire mouseleave/mouseenter in the same tick and the cancel beats the hide regardless of the
   * delay. The 150ms here is a UX cushion for the "cursor leaves the styles area entirely" case — it stops
   * the popover from flickering off the moment the user brushes past an item, and gives them a beat to come
   * back if they overshoot.
   */
  scheduleHideCodePopover(popoverEl: HTMLElement) {
    clearTimeout(this.codePopoverShowTimeout);
    clearTimeout(this.codePopoverHideTimeout);
    this.codePopoverHideTimeout = window.setTimeout(() => {
      popoverEl.hidePopover?.();
      if (this.codePopoverShown === popoverEl) {
        this.codePopoverShown = null;
      }
    }, 150);
  }

  /**
   * Cancels a scheduled hide. Called when the cursor (re)enters the popover host so the user can interact
   * with the popover content (e.g. scroll), and from `onPopoverHostMouseLeave` when the cursor returns to
   * the anchor item without triggering a fresh `item.mouseenter`.
   */
  cancelHideCodePopover() {
    clearTimeout(this.codePopoverHideTimeout);
  }

  /**
   * Mouseleave handler for the popover host. The host is a DOM descendant of its anchor list item, but it renders
   * in the top layer via popover="manual". When the cursor crosses from the host back into the item, item.mouseenter
   * does NOT fire (the item was already "under" the cursor via its host descendant per the UI Events spec), so without
   * this check we would miss the cancel signal and hide the popover 150ms later. Detect that path here and keep the
   * popover open; otherwise fall through to the normal scheduled-hide behavior.
   */
  onPopoverHostMouseLeave(event: MouseEvent, popoverEl: HTMLElement) {
    const target = event.relatedTarget as Node | null;
    if (target) {
      const item = popoverEl.closest('mat-list-item');
      if (item && item.contains(target)) {
        this.cancelHideCodePopover();
        return;
      }
    }
    this.scheduleHideCodePopover(popoverEl);
  }

  isHiddenStyle(styleName: string): boolean {
    return this.styleFilterValue && !styleName.toLowerCase().includes(this.styleFilterValue);
  }

  resetStyleFilter(): void {
    this.styleFilterValue = '';
  }

  toggleStyleFilter(): void {
    this.showStyleFilter = !this.showStyleFilter;
    if (!this.showStyleFilter) {
      this.resetStyleFilter();
    }
  }
}
